// enums

import type { ReactNode } from 'react';

import type { CustomizablePdfDeal, FuelSavingData, PdfPerson, PdfTradeIn, PdfVehicle } from '@makemydeal/dr-platform-types';
import type { DealType, DealVisibility, IncentiveType } from '@makemydeal/dr-shared-bff-types';
import type { ApiRequestStatus, Dealer } from '@makemydeal/dr-shared-types';
import type { DealXG, PrintingCustomization, WorkflowStatus } from '@makemydeal/dr-sales-checkin-types';
import type { DealGridSelection } from '@makemydeal/dr-services-pdf-shared';

// Dependency-injection contract: the host app implements these against its own store so this
// activity stays app-agnostic (mirrors the dealership-picker AdditionalSelectors pattern).
// TODO: move DealersInfo to the DealProposals folder in sales-checkin-types once it is created.
export type DealersInfo = {
    id: number;
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    phoneNumber?: string;
};

export type AdditionalSelectors = {
    getDealPdfEndpoint: (state: any) => string;
    getDealProposalEndpoint: (state: any) => string;
    getDealersInfo: (state: any) => DealersInfo;
    getDealVisibility: (state: any) => DealVisibility | undefined;
    /** The deal's current dealXgVersion — stamped onto the PDF fetch so the reducer
     * can record which version `dealPdfUrl` is pinned to. */
    getDealXgVersion: (state: any) => string;
    getDealXgId: (state: any) => string;
    getActiveDeal: (state: any) => DealXG;
    getPublishedBy: (state: any) => string;
    getPublishedVersionComment: (state: any) => string;
    isOfferCommentsEnabled: (state: any) => boolean;
    getPublishedVersion: (state: any) => string | undefined;
    getPublishedProposalId: (state: any) => string | undefined;
    getWorkflowStatus: (state: any) => WorkflowStatus;
    isFullPageScrollEnabled: (state: any) => boolean;
    isMultiScenarioOfferEnabled: (state: any) => boolean;
    /** True when this deal's current structured version was created by AMD/AMD Elite/a Tier-1 OEM site (F302128 Part A). */
    isAmdOriginated: (state: any) => boolean;
    /** Dealer identity/address/branding, sent alongside `dealData` so the generated PDF's dealer block isn't blank (F302128 Part A). */
    getDealerInfo: (state: any) => Partial<Dealer>;
    getSalesPerson: (state: any) => PdfPerson | undefined;
    getBuyerInfo: (state: any) => PdfPerson;
    getCoBuyerName: (state: any) => string | undefined;
    getTradeIns: (state: any) => PdfTradeIn[];
};

// Dependency-injection contract: the host app implements these against its own store so the
// activity can trigger host-owned effects (deal save, buyer acceptance, visibility) without
// owning duplicate action types that only worked via a string-match with the host's store.
export type HostActions = {
    updateDealSummary: () => { type: string };
    setDealAcceptedByBuyer: () => { type: string };
    updateActiveDealVisibilityStatus: (visibility: DealVisibility) => { type: string };
};

// state
export type RequestProposalState = {
    selectedDealTypes: DealType[];
    selectedIncentives: IncentiveType[];
    notes: string;
    isRequestChangeModalOpen: boolean;
    dealPdfUrl: string;
    dealPdfVersion?: string;
    showConfetti: boolean;
    dealPdfFetchStatus: ApiRequestStatus.Idle | ApiRequestStatus.Loading | ApiRequestStatus.Error;
    isDealPdfLoading: boolean;
    requestStatus: ApiRequestStatus;
};

export type IncentivesSectionProps = {
    selectedIncentives: IncentiveType[];
    onToggle: (incentive: IncentiveType[]) => void;
};

export type PaymentOptionsSectionProps = {
    selectedDealTypes: DealType[];
    onToggle: (dealType: DealType[]) => void;
};

export type NotesSectionProps = {
    notes: string;
    onChange: (value: string) => void;
};

export type SendRequestButtonProps = {
    isSubmitting: boolean;
    onClick: () => void;
};

export type CustomerInfo = {
    name: string;
    coBuyerName?: string;
    email?: string;
    phone?: string;
    address?: string;
};

export type PaymentBreakdownProps = {
    title: string;
    payment: string;
    paymentDetail?: string;
};

export type ParticipantsInfoProps = {
    salesPerson?: string;
    customer?: CustomerInfo;
};

export type VehicleSpec = {
    label: string;
    value: string;
};

export type VehicleInfoDetails = {
    title: string;
    imageUrl: string;
    specColumns: VehicleSpec[][];
};

export type VehicleInfoProps = {
    vehicle?: VehicleInfoDetails;
};

export type FuelEconomyMetric = {
    label: string;
    value: string;
};

export type FuelEconomyProps = {
    metrics?: FuelEconomyMetric[];
};

export type TradeInDetail = {
    label: string;
    value: string;
};

export type TradeInData = {
    title: string;
    vehicleName: string;
    vehicleValue: string;
    mileage: string;
    vin: string;
    details: TradeInDetail[];
    total: TradeInDetail;
};

export type TradeInProps = TradeInData & {
    'data-testid'?: string;
};

export type DealTypeCardProps = {
    dealType: DealType;
    children?: ReactNode;
    amount?: string;
    amountDescription?: string;
};

export type OfferComment = {
    comment: string;
    meta: string;
};

/**
 * Proposal types
 */
export enum ProposalLayoutType {
    SINGLE_COLUMN = 'SINGLE_COLUMN',
    MULTI_COLUMN = 'MULTI_COLUMN'
}

export type ProposalCustomerInfo = {
    customerName: string;
    coBuyerName?: string;
    contactInfo?: string;
};

export type DealProposalsView = {
    requestStatus: ApiRequestStatus;
    layoutType: ProposalLayoutType;
    salesPersonName: string;
    customerInfo: ProposalCustomerInfo;
    vehicle?: PdfVehicle;
    fuelSavingData?: FuelSavingData;
    trades: PdfTradeIn[];
    scenarios: CustomizablePdfDeal[];
    activeScenario?: CustomizablePdfDeal;
    activeSelection?: DealGridSelection;
    onGridCellChange: (selection: DealGridSelection) => void;
    printConfig?: PrintingCustomization;
};
